import React, {
  createContext, useContext, useEffect, useState,
} from "react";

import fetchAccessStatus from "../../../api/grace-period";

import { normalizeCustomerType } from "../../../utils/common";
import {
  areResultsLocked,
  GRACE_PERIOD_UPDATED_EVENT,
  isAccountFrozen,
  isGraceActive,
  readGracePeriodFromSession,
} from "../utils";

const defaultAccessValue = {
  customer_type: "normal",
  grace_period_status: null,
  grace_ends_on: null,
  grace_days_left: null,
  grace_amount_due: null,
  grace_days_overdue: null,
  invoice_id: null,
  invoice_amount_due: null,
  invoice_pdf_path: null,
  total_credits: null,
  hasLoadedAccessStatus: false,
  isGraceActive: false,
  isFrozen: false,
  areResultsLocked: false,
};

export const AccessStatusContext = createContext(defaultAccessValue);

function buildAccessValue(payload = {}, current = {}) {
  const customerType = normalizeCustomerType(payload.customer_type);
  const status = payload.grace_period_status ?? null;

  return {
    customer_type: customerType,
    grace_period_status: status,
    grace_ends_on: payload.grace_ends_on ?? null,
    grace_days_left: payload.grace_days_left ?? null,
    grace_amount_due: payload.grace_amount_due ?? null,
    grace_days_overdue: payload.grace_days_overdue ?? null,
    invoice_id: payload.invoice_id ?? null,
    invoice_amount_due: payload.invoice_amount_due ?? null,
    invoice_pdf_path: payload.invoice_pdf_path ?? null,
    total_credits: payload.total_credits ?? current.total_credits ?? null,
    hasLoadedAccessStatus: current.hasLoadedAccessStatus ?? false,
    isGraceActive: isGraceActive(status, customerType),
    isFrozen: isAccountFrozen(status, customerType),
    areResultsLocked: areResultsLocked(status, customerType),
  };
}

export function AccessStatusProvider({ children }) {
  const [access, setAccess] = useState(defaultAccessValue);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const applyStoredAccess = () => {
      if (isMounted) {
        setAccess((current) => buildAccessValue(readGracePeriodFromSession(), current));
      }
    };

    applyStoredAccess();
    window.addEventListener(GRACE_PERIOD_UPDATED_EVENT, applyStoredAccess);

    fetchAccessStatus(controller.signal).then((response) => {
      if (!isMounted || response.canceled) {
        return;
      }
      setAccess((current) => ({
        ...buildAccessValue({
          ...readGracePeriodFromSession(),
          total_credits: response.total_credits,
        }, current),
        hasLoadedAccessStatus: true,
      }));
    });

    return () => {
      isMounted = false;
      controller.abort();
      window.removeEventListener(GRACE_PERIOD_UPDATED_EVENT, applyStoredAccess);
    };
  }, []);

  return (
    <AccessStatusContext.Provider value={access}>
      {children}
    </AccessStatusContext.Provider>
  );
}

export function useAccessStatus() {
  return useContext(AccessStatusContext);
}
